import type React from "react";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { client } from "@duress/shared";
import { useUpdateClientInfo } from "./useClients";

interface Props {
  clientId: string;
  clientName: string;
  current: Pick<
    client.ClientListItem,
    | "region"
    | "pathfinderUsername"
    | "googleDriveUrl"
    | "canvaUrl"
    | "contractEndDate"
    | "falconCount"
    | "eagleCount"
    | "appOnly"
    | "tablets"
    | "simProvider"
    | "customEscalation"
    | "receivesMonthlyReports"
    | "arr"
  >;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ClientInfoDialog({
  clientId,
  clientName,
  current,
  open,
  onOpenChange,
}: Props): React.JSX.Element {
  const [name, setName] = useState(clientName);
  const [pathfinderUsername, setPathfinderUsername] = useState(current.pathfinderUsername ?? "");
  const [googleDriveUrl, setGoogleDriveUrl] = useState(current.googleDriveUrl ?? "");
  const [canvaUrl, setCanvaUrl] = useState(current.canvaUrl ?? "");
  const [contractEndDate, setContractEndDate] = useState(current.contractEndDate ?? "");
  const [arr, setArr] = useState<string>(current.arr?.toString() ?? "");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const update = useUpdateClientInfo(clientId);

  useEffect(() => {
    if (open) {
      setName(clientName);
      setPathfinderUsername(current.pathfinderUsername ?? "");
      setGoogleDriveUrl(current.googleDriveUrl ?? "");
      setCanvaUrl(current.canvaUrl ?? "");
      setContractEndDate(current.contractEndDate ?? "");
      setArr(current.arr?.toString() ?? "");
      setSubmitError(null);
    }
  }, [open, current, clientName]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitError(null);
    const parsedArr = arr.trim() === "" ? null : parseInt(arr.replace(/[^0-9]/g, ""), 10);
    if (arr.trim() !== "" && (parsedArr === null || isNaN(parsedArr))) {
      setSubmitError("ARR must be a whole dollar amount.");
      return;
    }
    try {
      await update.mutateAsync({
        name: name.trim() || undefined,
        region: current.region,
        pathfinderUsername: pathfinderUsername.trim() || null,
        googleDriveUrl: googleDriveUrl.trim() || null,
        canvaUrl: canvaUrl.trim() || null,
        contractEndDate: contractEndDate || null,
        falconCount: current.falconCount,
        eagleCount: current.eagleCount,
        appOnly: current.appOnly,
        tablets: current.tablets,
        simProvider: current.simProvider,
        customEscalation: current.customEscalation,
        receivesMonthlyReports: current.receivesMonthlyReports,
        arr: parsedArr,
      });
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Couldn't save. Try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>Edit client</DialogTitle>
        <DialogDescription>Update the client name, info, and links.</DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label htmlFor="client-name">Client name</Label>
          <Input
            id="client-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Corp"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="arr">ARR (annual contract value)</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
            <Input
              id="arr"
              type="text"
              inputMode="numeric"
              value={arr}
              onChange={(e) => setArr(e.target.value)}
              placeholder="e.g. 120000"
              className="pl-7"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="contract-end-date">Contract end date</Label>
          <Input
            id="contract-end-date"
            type="date"
            value={contractEndDate}
            onChange={(e) => setContractEndDate(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pathfinder-username">Pathfinder username</Label>
          <Input
            id="pathfinder-username"
            value={pathfinderUsername}
            onChange={(e) => setPathfinderUsername(e.target.value)}
            placeholder="e.g. acme_coffee"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="google-drive-url">Google Drive folder</Label>
          <Input
            id="google-drive-url"
            type="url"
            value={googleDriveUrl}
            onChange={(e) => setGoogleDriveUrl(e.target.value)}
            placeholder="https://drive.google.com/..."
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="canva-url">Canva folder</Label>
          <Input
            id="canva-url"
            type="url"
            value={canvaUrl}
            onChange={(e) => setCanvaUrl(e.target.value)}
            placeholder="https://www.canva.com/..."
          />
        </div>
        {submitError ? (
          <p className="text-sm text-destructive">{submitError}</p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={update.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
